import { useEffect, useRef, useCallback } from "react";
import { wsClient } from "./wsClient";

export function useTopic<T = unknown>(topic: string) {
  const listenersRef = useRef<Set<(payload: T) => void>>(new Set());

  const bridgeHandlerRef = useRef<(payload: T) => void>((payload: T) => {
    listenersRef.current.forEach((fn) => fn(payload));
  });

  /* =======================
     Subscription lifecycle
     ======================= */

  useEffect(() => {
    wsClient.connect();
    wsClient.subscribe(topic, bridgeHandlerRef.current);

    return () => {
      wsClient.unsubscribe(topic, bridgeHandlerRef.current);
    };
  }, [topic]);

  /* =======================
     Listener registration
     ======================= */

  const onMessage = useCallback((fn: (payload: T) => void) => {
    listenersRef.current.add(fn);

    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  /* =======================
     Publish
     ======================= */

  const publish = useCallback(
    (payload: T) => {
      wsClient.publish(topic, payload);
    },
    [topic],
  );

  /* =======================
     Optional command sender
     ======================= */

  const sendCommand = useCallback(
    <C = unknown,>(command: string, payload?: C, meta?: C) => {
      wsClient.sendCommand(command, payload, meta);
    },
    [],
  );

  return {
    onMessage,
    publish,
    sendCommand,
  };
}
